import type {createBusinessRuntime} from '../../../packages/runtime/business-runtime.ts';
import type {ServiceMode} from '../../../packages/contracts/operating-context.ts';
import type {
  HoldSmtP01WorkspaceCommand,
  RetrieveSmtP01WorkspaceCommand,
  SmtP01ActiveWorkItemViewModel,
  SmtP01ActiveWorkListViewModel,
  SmtP01WorkspaceHoldResult,
  SmtP01WorkspaceMutationContext,
  SmtP01WorkspaceRetrieveResult,
  SmtP01WorkspaceViewModel,
} from '../../../packages/runtime/smt-p01-workspace-contract.ts';
import type {ActiveWorkHandlerContext} from '../../../packages/runtime/active-work-custody.ts';

type BusinessRuntime = ReturnType<typeof createBusinessRuntime>;
type NextId = (kind: 'line'|'intake'|'order'|'idempotency'|'review') => string;

export interface SmmActiveWorkPortConfig {
  readonly runtime: BusinessRuntime;
  readonly operatorId: string;
  readonly deviceId: string;
  readonly sessionRef?: string;
  readonly initialServiceMode?: ServiceMode;
  readonly now: () => string;
  readonly nextId: NextId;
}

export interface SmmActiveWorkRef {
  readonly workspaceId: string;
  readonly workspaceRevision: number;
  readonly custodyRevision: number;
}

const requiredText = (value: string, code: string): string => {
  const normalized = value?.trim();
  if (!normalized) throw new Error(code);
  return normalized;
};

export function createSmmActiveWorkPort(config: SmmActiveWorkPortConfig) {
  const operatorId = requiredText(config.operatorId, 'ACTIVE_WORK_ACTOR_REQUIRED');
  const deviceId = requiredText(config.deviceId, 'ACTIVE_WORK_DEVICE_REQUIRED');
  const sessionRef = config.sessionRef?.trim() || undefined;
  let currentWorkspaceId: string | null = null;

  const handler = (at = config.now()): ActiveWorkHandlerContext => ({
    actorId: operatorId,
    surface: 'smm',
    deviceId,
    at,
    ...(sessionRef ? {sessionRef} : {}),
  });

  const currentWorkspace = (): SmtP01WorkspaceViewModel | null =>
    currentWorkspaceId ? config.runtime.smtP01Workspace.snapshot(currentWorkspaceId) : null;

  const ensureWorkspace = (): SmtP01WorkspaceViewModel => {
    const current = currentWorkspace();
    if (current) return current;
    const at = config.now();
    const workspaceId = `smm:${deviceId}:${config.nextId('idempotency')}`;
    const opened = config.runtime.smtP01Workspace.openActiveWorkspace({
      workspaceId,
      handler: handler(at),
      initialMode: 'normal',
      initialServiceMode: config.initialServiceMode ?? 'takeaway',
    });
    currentWorkspaceId = opened.workspaceId;
    return opened;
  };

  const mutationContext = (): SmtP01WorkspaceMutationContext => {
    const workspace = ensureWorkspace();
    return {
      workspaceId: workspace.workspaceId,
      actorId: operatorId,
      expectedRevision: workspace.revision,
      idempotencyKey: config.nextId('idempotency'),
      at: config.now(),
      surface: 'smm',
      deviceId,
      ...(sessionRef ? {sessionRef} : {}),
    };
  };

  const readActiveWork = (): Promise<SmtP01ActiveWorkListViewModel> =>
    config.runtime.smtP01Workspace.activeWork();

  const holdCurrent = async (): Promise<SmtP01WorkspaceHoldResult> => {
    const workspace = currentWorkspace();
    if (!workspace) return {kind: 'rejected', code: 'ACTIVE_WORK_NOT_FOUND'};
    if (!workspace.custody) return {kind: 'rejected', code: 'ACTIVE_WORK_CUSTODY_REQUIRED'};
    const command: HoldSmtP01WorkspaceCommand = {
      ...mutationContext(),
      expectedCustodyRevision: workspace.custody.custodyRevision,
    };
    const result = await config.runtime.smtP01Workspace.holdWorkspace(command);
    if (result.kind === 'accepted') currentWorkspaceId = null;
    return result;
  };

  const retrieve = async (ref: SmmActiveWorkRef): Promise<SmtP01WorkspaceRetrieveResult> => {
    if (currentWorkspaceId) return {kind: 'rejected', code: 'SMM_ACTIVE_WORK_CURRENT_WORKSPACE_PRESENT'};
    const at = config.now();
    const command: RetrieveSmtP01WorkspaceCommand = {
      workspaceId: ref.workspaceId,
      actorId: operatorId,
      surface: 'smm',
      deviceId,
      expectedWorkspaceRevision: ref.workspaceRevision,
      expectedCustodyRevision: ref.custodyRevision,
      idempotencyKey: config.nextId('idempotency'),
      at,
      ...(sessionRef ? {sessionRef} : {}),
    };
    const result = await config.runtime.smtP01Workspace.retrieveWorkspace(command);
    if (result.kind === 'accepted') currentWorkspaceId = result.snapshot.workspaceId;
    return result;
  };

  return {
    currentWorkspace,
    ensureWorkspace,
    mutationContext,
    readActiveWork,
    holdCurrent,
    retrieve,
    currentWorkspaceId: () => currentWorkspaceId,
    handler,
    clearAfterFinalization(workspaceId: string): void {
      if (currentWorkspaceId === workspaceId) currentWorkspaceId = null;
    },
  };
}

export type SmmActiveWorkPort = ReturnType<typeof createSmmActiveWorkPort>;
export type {SmtP01ActiveWorkItemViewModel};
